import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type DistributorGroupUserPagedResponse = components['schemas']['GetUsersByDistributorGroupEndpoint_DistributorGroupUserPagedResponse'];

export interface UseUsersByDistributorGroupReturn extends BaseQuery {
  data: DistributorGroupUserPagedResponse | null | undefined;
}

export function useUsersByDistributorGroup(
  distributorGroupId: number | undefined,
  page: number = 1,
  pageSize: number = 10,
): UseUsersByDistributorGroupReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getUsersByDistributorGroup = async (organizationId: number, distributorGroupId: number, page: number, pageSize: number) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users', {
      params: {
        path: { organizationId, distributorGroupId },
        query: { Page: page, PageSize: pageSize },
      },
    });

    return data as DistributorGroupUserPagedResponse;
  };

  const query = useQuery({
    queryKey: ['usersByDistributorGroup', organizationId, distributorGroupId, page, pageSize],
    queryFn: () => getUsersByDistributorGroup(organizationId, distributorGroupId!, page, pageSize),
    enabled: !!distributorGroupId && distributorGroupId > 0 && organizationId > 0,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = query;

  return {
    data,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
